import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import PdfRender from './PdfRender';
import { Markdownit, DataType } from '@mxmweb/rtext';
import type { AppTheme, Annotation, MDAnnotation, TableAnnotation, ToolsConfig } from './types';

// 文件类型枚举
type FileType =
  | 'pdf'
  | 'image'
  | 'text'
  | 'markdown'
  | 'pdf_slides'
  | 'markdown_table'
  | 'html'
  | 'unknown';

// Fviewer数据接口定义
// 自定义组件接口
export interface CustomizeComponents {
  LoadingComponent?: React.ComponentType<{
    status: string;
    theme: AppTheme;
  }>;
  ErrorComponent?: React.ComponentType<{
    error: string;
    theme: AppTheme;
  }>;
}

export interface FviewerData {
  content: any; // 文件内容
  fileName?: string; // 可选：文件名
  fileType?: string; // 可选：文件类型
  totalPages?: number; // 可选：总页数
  pdfStartPage?: number; // 可选：PDF文档的起始页码（用于分页PDF）
}

// Fviewer组件属性接口
export interface FviewerProps {
  // 数据相关
  data: FviewerData; // 文件数据
  annotationData?: (Annotation | MDAnnotation | TableAnnotation)[]; // 统一的标注数据
  totalPage?: number;
  currentPage?: number;
  scale?: number; // 缩放比例
  initialZoom?: number; // 初始缩放比例
  rotation?: number; // 旋转角度
  setScale?: (scale: number) => void;
  // 事件回调
  eventsEmit?: (name: string, data?: any, innerFn?: any) => void;

  // 样式配置
  styles?: {
    theme?: AppTheme;
    mode?: 'light' | 'dark';
  };

  // 工具配置
  tools?: ToolsConfig;

  // 自定义组件
  customComponents?: CustomizeComponents;

  // 自定义类名
  className?: string;
  contentClass?: string;
}

// 样式组件
const ContentContainer = styled.div<{ $theme: AppTheme }>`
  flex: 1;
  background: ${props => props.$theme.colors.background};
  position: relative;
  display: flex;
  flex-direction: column;
  scroll-behavior: smooth;

  min-height: 0; /* 确保flex子元素可以收缩 */
  height: calc(100% - 1px); /* 明确的高度设置，减去header高度 */
  /* 支持大尺寸内容的滚动 */
  overflow-x: auto;
  overflow-y: auto; /* 改为auto，允许垂直滚动 */
`;

const LoadingContainer = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.$theme.colors.disabledText};
`;

const ErrorContainer = styled.div<{ $theme: AppTheme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.$theme.colors.error};
  text-align: center;
  padding: 20px;
`;

// Markdown标注样式
const markdownAnnotationStyles = `
  .md-annotation {
    position: relative;
    display: inline;
    cursor: pointer;
    transition: opacity 0.2s;
    background-color: var(--highlight-color, #FFE599);
  }

  .md-annotation:hover {
    opacity: 0.8;
  }

  /* 表格标注样式 */
  .table-annotation {
    background-color: var(--table-highlight-color, rgba(255, 229, 153, 0.5));
  }

  /* markdown_table模式下的表格样式 */
  .markdown-table-mode table {
    overflow: visible !important;
    max-width: none !important;
    width: auto !important;
    table-layout: auto !important;
  }

  .markdown-table-mode table,
  .markdown-table-mode table * {
    overflow: visible !important;
  }

  .markdown-table-mode .table-container{
    margin: 0 !important;
    overflow: auto !important;
    width: 100% !important;
    min-width: 100% !important;
  }

  /* 确保表格单元格不会被压缩 */
  .markdown-table-mode table td,
  .markdown-table-mode table th {
    white-space: nowrap !important;
    min-width: 50px !important;
  }

  /* 移除fit-content，使用100%宽度确保内容能正常显示 */
  .markdown-table-mode div{
    width: fit-content !important;
  }

  /* 为表格添加最小宽度，确保内容不被压缩 */
  .markdown-table-mode table {
    min-width: fit-content !important;
  }

  .markdown-table-mode.markdown-scrollbar::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }


  /* 自定义滚动条样式 */
  .markdown-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .markdown-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  .markdown-scrollbar::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  .markdown-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a9a9a;
  }
`;

// 默认主题
const defaultTheme: AppTheme = {
  colors: {
    primary: '#007bff',
    secondary: '#6c757d',
    success: '#008000',
    warning: '#FFA500',
    error: '#FF0000',
    info: '#0000FF',
    background: '#f8f9fa',
    text: '#343a40',
    border: '#dee2e6',
    disabled: '#808080',
    disabledBackground: '#F5F5F5',
    disabledText: '#808080',
  },
  space: {
    sidebar: 'md',
    size: 'md',
    radius: 'md',
    padding: 'md',
    margin: 'md',
    shadow: 'md',
    lineHeight: 'md',
  },
};

/**
 * 检测文件类型
 */
const detectFileType = (data: any): FileType => {
  console.log('=== detectFileType 开始 ===');
  console.log('输入数据:', data);

  if (!data) {
    console.log('数据为空，返回 unknown');
    return 'unknown';
  }

  const fileName = data.fileName || '';
  const fileType = data.fileType || '';
  const content = data.content;

  console.log('检测参数:', { fileName, fileType, content });

  // 优先使用 fileType 参数进行检测，这是最准确的
  if (fileType === 'pdf' || fileType === 'pdf_slides') {
    console.log('检测到PDF文件');
    return fileType as FileType;
  }
  if (fileType === 'image') {
    console.log('检测到图片文件');
    return 'image';
  }
  if (fileType === 'markdown_table') {
    console.log('检测到Markdown表格文件');
    return 'markdown_table';
  }
  if (fileType === 'markdown') {
    console.log('检测到Markdown文件');
    return 'markdown';
  }
  if (fileType === 'html') {
    console.log('检测到HTML文件');
    return 'html';
  }
  if (fileType === 'text') {
    console.log('检测到文本文件');
    return 'text';
  }

  // 如果 fileType 没有明确指定，则根据文件名和内容进行推断
  if (fileName.toLowerCase().endsWith('.pdf') || content?.type === 'pdf') {
    console.log('检测到PDF文件');
    return 'pdf';
  }
  if (fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/) || content?.type === 'image') {
    console.log('检测到图片文件');
    return 'image';
  }
  if (fileName.toLowerCase().endsWith('.md') || content?.type === 'markdown') {
    console.log('检测到Markdown文件');
    return 'markdown';
  }
  if (fileName.toLowerCase().match(/\.(html|htm)$/) || content?.type === 'html') {
    console.log('检测到HTML文件');
    return 'html';
  }
  if (fileName.toLowerCase().match(/\.(txt|log|json|xml|css|js|ts)$/) || content?.type === 'text') {
    console.log('检测到文本文件');
    return 'text';
  }

  console.log('未识别的文件类型，返回 unknown');
  return 'unknown';
};

/**
 * 渲染PDF内容
 */
const renderPdfContent = (
  data: any,
  props: FviewerProps,
  isSlidesMode: boolean = false,
  setScale: ((scale: number) => void) | undefined = undefined
) => {
  console.log(
    '计算下，renderPdfContent:',
    'pdfStartPage:' + data.pdfStartPage,
    'currentPage:' + props.currentPage
  );
  console.log('=== renderPdfContent 开始 ===');
  console.log('PDF数据:', data);
  console.log('PDF文档:', data.content?.document);
  console.log('PDF文档类型:', typeof data.content?.document);
  console.log('PDF文档是否为null:', data.content?.document === null);
  console.log('PDF文档是否为undefined:', data.content?.document === undefined);
  console.log(
    'PDF文档方法:',
    data.content?.document
      ? Object.getOwnPropertyNames(Object.getPrototypeOf(data.content.document))
      : '无文档'
  );
  console.log(
    'PDF文档属性:',
    data.content?.document ? Object.keys(data.content.document) : '无文档'
  );

  // 计算PDF文档的起始页码（用于分页PDF）
  const pdfStartPage = data.pdfStartPage || 1;
  console.log('PDF起始页:', pdfStartPage);

  // 过滤出PDF标注数据
  const pdfAnnotations = (props.annotationData || []).filter(
    anno => 'pageNumber' in anno && 'x' in anno && 'y' in anno
  ) as Annotation[];

  console.log('=== PDF标注数据 ===');
  console.log('原始annotationData:', props.annotationData);
  console.log('过滤后的pdfAnnotations:', pdfAnnotations);

  // 根据文件类型设置默认缩放值
  const defaultScale = isSlidesMode ? 0.82 : 1;

  // 优先使用initialZoom，然后是scale，最后是默认值
  const finalScale = props.initialZoom || props.scale || defaultScale;

  return (
    <PdfRender
      pdfDocument={data.content?.document || null}
      currentPage={props.currentPage || 1}
      pdfStartPage={pdfStartPage}
      scale={finalScale} // 使用计算后的最终缩放值
      setScale={setScale}
      annotations={pdfAnnotations}
      isAnnotating={false} // PDF模式关闭标注功能
      isSlidesMode={isSlidesMode}
      onPageChange={
        props.eventsEmit
          ? (pageNumber: number) => props.eventsEmit?.('pageChange', { pageNumber })
          : undefined
      }
      onScaleChange={
        props.eventsEmit
          ? (scale: number) => props.eventsEmit?.('scaleChange', { scale })
          : undefined
      }
      onAnnotationChange={
        props.eventsEmit
          ? (annotation: Annotation) => props.eventsEmit?.('annotationChange', { annotation })
          : undefined
      }
      onAnnotationDelete={
        props.eventsEmit
          ? (annotationId: string) => props.eventsEmit?.('annotationDelete', { annotationId })
          : undefined
      }
      styles={props.styles}
    />
  );
};

/**
 * 渲染图片内容
 */
const renderImageContent = (data: any, props: FviewerProps) => {
  console.log('=== Fviewer renderImageContent ===');
  console.log('data:', data);
  console.log('data.content:', data.content);
  console.log('data.content?.url:', data.content?.url);
  console.log('rotation:', props.rotation);

  const imageUrl = data.content?.url || data.content;
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    console.log('图片加载完成:', imageUrl);
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    console.error('图片加载失败:', imageUrl);
    setImageLoading(false);
    setImageError(true);
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: '20px',
        overflow: 'auto',
        position: 'relative',
        backgroundColor: '#272727', // 图片模式深色背景
      }}
    >
      {imageLoading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              border: `3px solid ${props.styles?.theme?.colors?.border || '#dee2e6'}`,
              borderTop: `3px solid ${props.styles?.theme?.colors?.primary || '#007bff'}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div
            style={{
              fontSize: '12px',
              color: props.styles?.theme?.colors?.text || '#343a40',
            }}
          >
            图片加载中...
          </div>
        </div>
      )}

      {imageError && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontSize: '14px',
              color: props.styles?.theme?.colors?.error || '#dc3545',
            }}
          >
            图片加载失败
          </div>
        </div>
      )}

      <img
        src={imageUrl}
        alt={data.fileName || '图片'}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          transform: `scale(${props.scale || 1}) rotate(${props.rotation || 0}deg)`,
          opacity: imageLoading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out, transform 0.2s ease-in-out',
        }}
      />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

/**
 * 渲染文本内容
 */
const renderTextContent = (data: any, props: FviewerProps) => {
  console.log('=== Fviewer renderTextContent ===');
  console.log('data:', data);
  console.log('data.content:', data.content);

  // 获取实际的文本内容 - 兼容多种数据结构
  let textContent = '';
  if (typeof data.content === 'string') {
    textContent = data.content;
  } else if (data.content?.text) {
    // 线上环境：{ type: 'text', text: '...' }
    textContent = data.content.text;
  } else if (data.content?.content) {
    // 本地环境：{ content: '...' }
    textContent = data.content.content;
  }
  console.log('实际文本内容:', textContent);

  // 使用 ref 来动态调整纸张高度
  const textRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  // 动态调整纸张高度的 useEffect
  useEffect(() => {
    if (textRef.current && paperRef.current && textContent) {
      // 延迟执行，确保内容完全渲染
      const timer = setTimeout(() => {
        const textHeight = textRef.current?.scrollHeight || 0;
        const minHeight = Math.max(textHeight + 80, 200); // 至少200px高度，加上padding

        if (paperRef.current) {
          paperRef.current.style.minHeight = `${minHeight}px`;
          console.log('文本纸张高度调整完成:', minHeight);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [textContent]);

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#f5f5f5', // 纸张背景色
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {/* 纸张容器 */}
      <div
        ref={paperRef}
        style={{
          width: '100%',
          maxWidth: '800px',
          backgroundColor: 'white',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
          borderRadius: '8px',
          padding: '40px',
          margin: '20px 0',
          position: 'relative',
          transform: `scale(${props.scale || 1})`,
          transition: 'transform 0.2s ease-in-out',
          transformOrigin: 'top center',
        }}
      >
        {/* 使用 rtext 组件渲染文本内容 */}
        <div
          ref={textRef}
          style={{
            width: '100%',
            overflow: 'visible',
          }}
        >
          <Markdownit
            dataSource={{
              type: DataType.TEXT,
              content: textContent,
            }}
            size="md"
            theme={{
              primaryColor: props.styles?.theme?.colors?.primary || '#007bff',
              backgroundColor: 'transparent',
              textColor: '#333',
              borderColor: 'transparent',
              borderRadius: '0',
              padding: '0',
            }}
            style={{
              border: 'none',
              background: 'transparent',
              width: '100%',
              height: 'auto',
              overflow: 'visible',
              whiteSpace: 'pre-line',
            }}
          />
        </div>

        {/* 简约的顶部装饰效果 */}
        <div
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '2px',
            background: 'linear-gradient(90deg, #e0e0e0 0%, #f0f0f0 50%, #e0e0e0 100%)',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        />
      </div>
    </div>
  );
};

/**
 * 渲染Markdown内容
 */
// 将 Markdown 渲染逻辑提取为真正的 React 子组件，避免在渲染过程中条件执行 Hook
const MarkdownContentView: React.FC<{
  data: any;
  parentProps: FviewerProps;
  isTableMode?: boolean;
}> = ({ data, parentProps, isTableMode = false }) => {
  const markdownRef = useRef<HTMLDivElement>(null);
  const annotationTimeoutRef = useRef<number | null>(null);
  const tableObserverRef = useRef<MutationObserver | null>(null);
  const tableReapplyInProgressRef = useRef<boolean>(false);
  const lastMutationTsRef = useRef<number>(0);
  const idleDisconnectTimerRef = useRef<number | null>(null);
  const [isAnnotating] = useState(false);

  const handleScroll = (scrollTop: number, scrollHeight: number) => {
    console.log('=== Markdownit滚动事件触发 ===');
    console.log('滚动位置:', { scrollTop, scrollHeight });

    if (parentProps.eventsEmit) {
      // 直接传递滚动信息给业务层，让业务层决定如何处理
      parentProps.eventsEmit('markdown_scroll', { scrollTop, scrollHeight });
    }
  };

  const handleScrollToTop = () => {
    console.log('=== Markdownit滚动到顶部 ===');
    if (parentProps.eventsEmit) {
      parentProps.eventsEmit('markdown_scroll_to_top');
    }
  };

  const handleScrollToBottom = () => {
    console.log('=== Markdownit滚动到底部 ===');
    if (parentProps.eventsEmit) {
      parentProps.eventsEmit('markdown_scroll_to_bottom');
    }
  };

  // Markdownit 渲染完成回调：仅在表格模式下一次性应用表格标注
  const handleMarkdownRenderedRef = useRef<(root: HTMLElement) => void>();
  handleMarkdownRenderedRef.current = (root: HTMLElement) => {
    try {
      if (!isTableMode) return;
      const content = typeof data.content === 'string' ? data.content : data.content?.content || '';
      if (!content || tableAnnotations.length === 0) return;
      const annoSig = JSON.stringify(tableAnnotations.map(a => ({ o: a.offsets, c: a.color })));
      const runtimeSig = `${content.length}:${content.substring(0, 64)}:${content.substring(content.length - 64)}|${annoSig}`;
      const prevSig = (root as any).dataset?.tableAnnoRuntimeSig;
      if (prevSig === runtimeSig) return;
      setTimeout(() => {
        applyTableAnnotations();
        if ((root as any).dataset) {
          (root as any).dataset.tableAnnoRuntimeSig = runtimeSig;
        }
      }, 50);
    } catch (e) {
      console.warn('[MarkdownRendered] apply failed', e);
    }
  };
  const handleMarkdownRendered = React.useCallback(
    (root: HTMLElement) => handleMarkdownRenderedRef.current?.(root),
    []
  );

  // 处理文本选择和高亮
  const handleTextSelection = useCallback(() => {
    if (!isAnnotating || !markdownRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    const text = range.toString().trim();
    if (!text) return;

    // 计算选中文本的绝对位置
    const container = markdownRef.current;
    let charCount = 0;
    let startOffset = 0;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: node => {
        // 跳过代码块内的文本
        if (node.parentElement?.closest('pre')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let node: Node | null = walker.nextNode();
    while (node) {
      if (node === range.startContainer) {
        startOffset = charCount + range.startOffset;
        break;
      }
      charCount += node.textContent?.length || 0;
      node = walker.nextNode();
    }

    console.log('选中文本信息:', {
      text,
      startOffset,
      charCount,
      container: range.startContainer.textContent,
    });

    const newAnnotation: MDAnnotation = {
      id: `md-anno-${Date.now()}`,
      start: startOffset,
      end: startOffset + text.length,
      color: '#FFE599',
    };

    console.log('创建新标注:', newAnnotation);
    if (parentProps.eventsEmit) {
      parentProps.eventsEmit('md_annotation_add', newAnnotation);
    }
    selection.removeAllRanges();
  }, [isAnnotating, parentProps.eventsEmit]);

  // 根据模式过滤标注数据
  const mdAnnotations = isTableMode
    ? [] // markdown_table模式不使用MDAnnotation
    : ((parentProps.annotationData || []).filter(
        anno => 'start' in anno && 'end' in anno && !('pageNumber' in anno)
      ) as MDAnnotation[]);

  // 过滤出Table标注数据
  const tableAnnotations = isTableMode
    ? ((parentProps.annotationData || []).filter(
        anno => 'offsets' in anno && Array.isArray(anno.offsets)
      ) as TableAnnotation[])
    : [];

  /**
   * 清空所有标注的函数
   * 移除所有标注元素，恢复原始文本和样式
   */
  const clearAllAnnotations = useCallback(() => {
    console.log('=== 开始清空所有标注 ===');

    if (!markdownRef.current) {
      console.log('markdownRef不存在，无法清空标注');
      return;
    }

    // 清空Markdown标注
    const existingMdAnnotations = markdownRef.current.querySelectorAll('.md-annotation');
    console.log('清空Markdown标注数量:', existingMdAnnotations.length);

    if (existingMdAnnotations.length > 0) {
      existingMdAnnotations.forEach(el => {
        const parent = el.parentNode;
        if (parent) {
          // 将标注元素替换为原始文本
          parent.replaceChild(document.createTextNode(el.textContent || ''), el);
        }
      });
    }

    // 不再清空表格背景色，避免已应用的表格高亮被重置

    console.log('所有标注已清空');
  }, []);

  // 清空表格标注的函数
  const clearTableAnnotations = useCallback(() => {
    if (!markdownRef.current) return;

    const tables = markdownRef.current.querySelectorAll('table');
    tables.forEach(table => {
      const allRows = Array.from(table.rows);
      allRows.forEach(row => {
        Array.from(row.cells).forEach(cell => {
          if (cell.getAttribute('data-table-annotated') === '1') {
            cell.style.removeProperty('background');
            cell.style.removeProperty('background-color');
            cell.removeAttribute('data-table-annotated');
            cell.removeAttribute('title');
          }
        });
      });
    });
    console.log('已清空所有表格标注');
  }, []);

  // 应用表格标注的函数
  const applyTableAnnotations = useCallback(() => {
    console.log('=== 开始应用表格标注 ===');
    console.log('tableAnnotations:', tableAnnotations);

    if (!markdownRef.current) {
      console.log('markdownRef不存在，退出');
      return;
    }

    // 先清空所有旧的表格标注
    clearTableAnnotations();

    if (!tableAnnotations.length) {
      console.log('没有表格标注数据，仅清空旧标注');
      return;
    }

    // 查找所有表格
    const tables = markdownRef.current.querySelectorAll('table');
    console.log('找到表格数量:', tables.length);
    if (tables.length === 0) {
      console.log('没有找到表格元素');
      return;
    }

    tableReapplyInProgressRef.current = true;
    tableAnnotations.forEach((annotation, i: number) => {
      const offsets = annotation.offsets;
      console.log('处理表格标注:', annotation);

      // 只判断 offsets 是否为 [ [number, number], ... ]
      if (
        !Array.isArray(offsets) ||
        !offsets.length ||
        !Array.isArray(offsets[0]) ||
        offsets[0].length !== 2
      ) {
        console.warn('[TableAnnotation] annotation.offsets is not valid', annotation);
        return;
      }

      offsets.forEach((offset: [number, number], index: number) => {
        const [row, col] = offset;
        if (typeof row !== 'number' || typeof col !== 'number') {
          console.warn('[TableAnnotation] offset is not [number, number]', offset);
          return;
        }

        // 遍历所有表格，找到对应的单元格
        tables.forEach((table, tableIndex) => {
          // 从表头开始计算，row=0 指向表头第一行
          const allRows = Array.from(table.rows);
          const rowEl = allRows[row];
          if (!rowEl) {
            console.warn('[TableAnnotation] row out of range', {
              row,
              tableIndex,
              totalRows: allRows.length,
            });
            return;
          }

          const cell = rowEl.cells?.[col] as HTMLTableCellElement | undefined;
          if (!cell) {
            console.warn('[TableAnnotation] cell is undefined', { row, col, tableIndex });
            return;
          }

          const targetColor = annotation.color || 'rgba(255, 229, 153, 0.5)';
          const computedBg = getComputedStyle(cell).backgroundColor;
          const alreadyAnnotated =
            cell.getAttribute('data-table-annotated') === '1' &&
            (computedBg === targetColor || cell.style.backgroundColor === targetColor);
          if (!alreadyAnnotated) {
            cell.style.setProperty('background', targetColor, 'important');
            cell.style.setProperty('background-color', targetColor, 'important');
            cell.setAttribute('data-table-annotated', '1');
          }
          if (annotation.content) cell.title = annotation.content;

          if (index === 0 && i === 0) {
            setTimeout(() => {
              cell.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
          }
        });
      });
    });

    requestAnimationFrame(() => {
      tableReapplyInProgressRef.current = false;
    });
  }, [tableAnnotations, clearTableAnnotations]);

  // 应用Markdown标注的函数
  const applyMarkdownAnnotations = useCallback(() => {
    console.log('=== 开始应用Markdown标注 ===');

    if (!markdownRef.current || !mdAnnotations.length) {
      console.log('条件不满足，跳过标注应用');
      return;
    }

    // 清除之前的定时器，避免重复应用
    if (annotationTimeoutRef.current) {
      clearTimeout(annotationTimeoutRef.current);
    }

    // 直接应用标注，不使用MutationObserver避免时序问题
    const applyMarkdownAnnotationsInner = () => {
      console.log('开始应用标注...');

      // 移除现有标注
      const existingAnnotations = markdownRef.current?.querySelectorAll('.md-annotation');
      console.log('移除现有标注数量:', existingAnnotations?.length || 0);
      existingAnnotations?.forEach(el => {
        const parent = el.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(el.textContent || ''), el);
        }
      });

      // 获取所有文本节点
      const walker = document.createTreeWalker(markdownRef.current!, NodeFilter.SHOW_TEXT, {
        acceptNode: node => {
          // 跳过代码块内的文本
          if (node.parentElement?.closest('pre')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      const textNodes: Text[] = [];
      let node: Text | null;
      while ((node = walker.nextNode() as Text)) {
        textNodes.push(node);
      }

      console.log('找到文本节点数量:', textNodes.length);

      if (textNodes.length === 0) {
        console.log('没有找到文本节点，延迟重试...');
        // 延迟重试，确保DOM完全渲染
        annotationTimeoutRef.current = setTimeout(() => {
          applyMarkdownAnnotationsInner();
        }, 200);
        return;
      }

      // 计算每个文本节点的起始位置
      let totalLength = 0;
      const nodePositions = textNodes.map(node => {
        const start = totalLength;
        totalLength += node.textContent?.length || 0;
        return {
          node,
          start,
          end: totalLength,
          text: node.textContent,
        };
      });

      console.log('文本节点位置:', nodePositions);
      console.log('总文本长度:', totalLength);

      // 应用标注
      const sortedAnnotations = [...mdAnnotations].sort((a, b) => b.start - a.start);
      console.log('排序后的标注:', sortedAnnotations);

      sortedAnnotations.forEach(annotation => {
        const { start, end, color } = annotation;
        console.log('处理标注:', { annotation, nodePositions });

        // 找到相关的文本节点
        const relevantNodes = nodePositions.filter(pos => start < pos.end && end > pos.start);

        console.log('相关节点数量:', relevantNodes.length);

        relevantNodes.forEach(({ node, start: nodeStart }) => {
          const localStart = Math.max(0, start - nodeStart);
          const localEnd = Math.min(node.textContent?.length || 0, end - nodeStart);

          console.log('本地位置:', {
            localStart,
            localEnd,
            nodeTextLength: node.textContent?.length,
          });

          if (localStart >= localEnd) {
            console.log('本地位置无效，跳过');
            return;
          }

          const before = node.textContent?.slice(0, localStart) || '';
          const annotatedText = node.textContent?.slice(localStart, localEnd) || '';
          const after = node.textContent?.slice(localEnd) || '';

          console.log('分割文本:', { before, annotatedText, after });

          const span = document.createElement('span');
          span.className = 'md-annotation';
          span.style.backgroundColor = color || '#FFE599';
          span.textContent = annotatedText;

          const fragment = document.createDocumentFragment();
          if (before) fragment.appendChild(document.createTextNode(before));
          fragment.appendChild(span);
          if (after) fragment.appendChild(document.createTextNode(after));

          node.parentNode?.replaceChild(fragment, node);
          console.log('成功应用标注:', annotatedText);
        });
      });
    };

    // 延迟应用标注，确保DOM完全渲染
    annotationTimeoutRef.current = setTimeout(() => {
      applyMarkdownAnnotationsInner();
    }, 100);
  }, [mdAnnotations]);

  // 应用标注高亮（表格模式改由 onRendered 触发，这里跳过）
  useEffect(() => {
    if (isTableMode) {
      console.log('[MarkdownContentView] table mode: skip effect, wait for onRendered');
      return;
    }
    // 获取实际的内容字符串
    const content = typeof data.content === 'string' ? data.content : data.content?.content || '';

    if (!content || typeof content !== 'string') {
      console.log('Markdown内容不是字符串类型，跳过标注应用');
      return;
    }

    console.log('=== 开始应用Markdown标注高亮 ===');
    console.log('markdownRef.current:', markdownRef.current);
    console.log('isTableMode:', isTableMode);
    console.log('mdAnnotations:', mdAnnotations);
    console.log('tableAnnotations:', tableAnnotations);
    console.log('data.content:', data.content);
    console.log('实际内容:', content);

    if (!markdownRef.current) {
      console.log('markdownRef不存在，退出标注应用');
      return;
    }

    // 表格模式不清空，避免已应用高亮被抹掉
    if (!isTableMode) {
      clearAllAnnotations();
    }

    // 表格模式下不从 effect 触发，交由 onRendered

    // 如果是普通markdown模式且有markdown标注，应用markdown标注
    if (!isTableMode && mdAnnotations.length > 0) {
      applyMarkdownAnnotations();
      return;
    }

    console.log('没有需要应用的标注');
  }, [isTableMode, tableAnnotations, mdAnnotations]);

  // 监听文本选择事件
  useEffect(() => {
    // 只在非表格模式下启用文本选择标注
    if (!isTableMode) {
      document.addEventListener('mouseup', handleTextSelection);
      return () => {
        document.removeEventListener('mouseup', handleTextSelection);
      };
    }
  }, [handleTextSelection, isTableMode]);

  // 调试：打印markdown内容信息
  useEffect(() => {
    // 获取实际的内容字符串
    const content = typeof data.content === 'string' ? data.content : data.content?.content || '';

    if (!content || typeof content !== 'string') {
      console.log('Markdown内容不是字符串类型，跳过调试信息打印');
      return;
    }

    console.log('=== Markdown内容信息 ===');
    console.log('isTableMode:', isTableMode);
    console.log('内容长度:', content.length || 0);

    // 安全检查：确保content是字符串类型才调用substring方法
    if (typeof content === 'string') {
      console.log('内容前100字符:', content.substring(0, 100));
      console.log('内容后100字符:', content.substring(content.length - 100));
    } else {
      console.log('内容不是字符串类型:', typeof content);
      console.log('内容值:', content);
    }

    console.log('标注数据:', mdAnnotations);
    console.log('表格标注数据:', tableAnnotations);
  }, [mdAnnotations, tableAnnotations, isTableMode]);

  // 监听内容变化，在表格模式下调整表格尺寸
  useEffect(() => {
    if (isTableMode && markdownRef.current) {
      // 延迟执行，确保Markdownit完全渲染完成
      const timer = setTimeout(() => {
        const tables = markdownRef.current?.querySelectorAll('table');
        if (tables && tables.length > 0) {
          console.log('调整表格尺寸，找到表格数量:', tables.length);

          tables.forEach((table, index) => {
            if (table instanceof HTMLTableElement) {
              // 确保表格有足够的宽度来显示内容
              table.style.width = '100%';
              table.style.minWidth = '100%';
              table.style.tableLayout = 'auto';

              // 调整表格容器
              const container = table.closest('.markdown-table-mode');
              if (container && container instanceof HTMLElement) {
                container.style.width = '100%';
                container.style.overflowX = 'auto';
              }

              console.log(`表格 ${index + 1} 尺寸调整完成`);
            }
          });
        }
      }, 500); // 延迟500ms确保渲染完成

      return () => clearTimeout(timer);
    }
  }, [isTableMode, data.content]);

  // 清理定时器与观察器
  useEffect(() => {
    return () => {
      if (annotationTimeoutRef.current) {
        clearTimeout(annotationTimeoutRef.current);
      }
      if (tableObserverRef.current) {
        tableObserverRef.current.disconnect();
        tableObserverRef.current = null;
      }
    };
  }, []);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        overflow: 'auto', // 改为auto，允许滚动条显示
      }}
      className={`markdown-container markdown-scrollbar ${isTableMode ? 'markdown-table-mode' : ''}`}
    >
      <style>{markdownAnnotationStyles}</style>
      <div ref={markdownRef}>
        <Markdownit
          dataSource={{
            type: DataType.MARKDOWN,
            content: (() => {
              // 兼容多种数据结构
              if (typeof data.content === 'string') {
                return data.content;
              } else if (data.content?.text) {
                // 线上环境：{ type: 'markdown', text: '...' }
                return data.content.text;
              } else if (data.content?.content) {
                // 本地环境：{ content: '...' }
                return data.content.content;
              }
              return '';
            })(),
          }}
          size="md"
          onScroll={!isTableMode ? handleScroll : undefined}
          onScrollToTop={!isTableMode ? handleScrollToTop : undefined}
          onScrollToBottom={!isTableMode ? handleScrollToBottom : undefined}
          onRendered={handleMarkdownRendered}
          theme={{
            primaryColor: parentProps.styles?.theme?.colors?.primary || '#007bff',
            backgroundColor: parentProps.styles?.theme?.colors?.background || '#f8f9fa',
            textColor: parentProps.styles?.theme?.colors?.text || '#343a40',
            borderColor: parentProps.styles?.theme?.colors?.border || '#dee2e6',
            borderRadius: isTableMode ? '0px' : '8px',
            padding: isTableMode ? '0px' : '20px',
          }}
          style={{
            height: '100%',
            border: 'none',
            background: 'transparent',
            overflowY: isTableMode ? 'visible' : 'scroll',
          }}
        />
      </div>
    </div>
  );
};

/**
 * 极简 markdown_table 渲染组件（绕过 Markdownit）
 * - 仅支持管道表语法，渲染单个 <table>
 * - 不引入内部状态与滚动回调
 */
const MinimalMarkdownTable: React.FC<{
  markdown: string;
  onRendered?: () => void;
  tableAnnotations?: TableAnnotation[];
}> = ({ markdown, onRendered, tableAnnotations = [] }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // 解析简单管道表
  const parseTable = (md: string): { headers: string[]; rows: string[][] } | null => {
    const lines = md.split(/\r?\n/).filter(l => l.trim().length > 0);
    // 找首个表头行和分隔行
    let headerLineIdx = -1;
    for (let i = 0; i < lines.length - 1; i++) {
      const a = lines[i].trim();
      const b = lines[i + 1].trim();
      if (a.startsWith('|') && a.endsWith('|') && b.replace(/\s|:|\|/g, '').match(/^[-]+$/)) {
        headerLineIdx = i;
        break;
      }
    }
    if (headerLineIdx === -1) return null;
    const headerLine = lines[headerLineIdx];
    const header = headerLine
      .split('|')
      .slice(1, -1)
      .map(s => s.trim());
    const bodyLines = [] as string[];
    for (let i = headerLineIdx + 2; i < lines.length; i++) {
      const l = lines[i].trim();
      if (!l.startsWith('|') || !l.endsWith('|')) break;
      bodyLines.push(l);
    }
    const rows = bodyLines.map(l =>
      l
        .split('|')
        .slice(1, -1)
        .map(s => s.trim())
    );
    return { headers: header, rows };
  };

  const table = React.useMemo(() => parseTable(markdown), [markdown]);

  // 渲染完成后应用表格标注（仅 DOM 操作，无 setState）
  useEffect(() => {
    const apply = () => {
      try {
        if (!rootRef.current) return;

        // 先清空所有旧的表格标注
        const tables = rootRef.current.querySelectorAll('table');
        tables.forEach(table => {
          const allRows = Array.from(table.rows);
          allRows.forEach(row => {
            Array.from(row.cells).forEach(cell => {
              if (cell.getAttribute('data-table-annotated') === '1') {
                cell.style.removeProperty('background');
                cell.style.removeProperty('background-color');
                cell.removeAttribute('data-table-annotated');
                cell.removeAttribute('title');
              }
            });
          });
        });

        if (!tableAnnotations.length) return;
        if (!tables || tables.length === 0) return;
        tableAnnotations.forEach((annotation, i) => {
          const offsets = annotation.offsets || [];
          offsets.forEach((offset, idx) => {
            const [row, col] = offset as [number, number];
            tables.forEach(tableEl => {
              // 从表头开始计算，row=0 指向表头第一行
              const allRows = Array.from(tableEl.rows);
              const rowEl = allRows[row] as HTMLTableRowElement | undefined;
              if (!rowEl) return;
              const cell = rowEl.cells?.[col] as HTMLTableCellElement | undefined;
              if (!cell) return;
              const color = annotation.color || 'rgba(255, 229, 153, 0.5)';
              const already = cell.getAttribute('data-table-annotated') === '1';
              if (!already) {
                cell.style.setProperty('background', color, 'important');
                cell.style.setProperty('background-color', color, 'important');
                cell.setAttribute('data-table-annotated', '1');
                if (annotation.content) cell.title = annotation.content;
              }
              if (i === 0 && idx === 0) {
                setTimeout(() => cell.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
              }
            });
          });
        });
      } catch (e) {
        console.warn('[MinimalMarkdownTable] apply annotations failed', e);
      }
    };

    const id = requestAnimationFrame(() => {
      apply();
      onRendered && onRendered();
    });
    return () => cancelAnimationFrame(id);
  }, [markdown, tableAnnotations, onRendered]);

  if (!table) {
    return (
      <div ref={rootRef} style={{ padding: '8px' }}>
        无法解析为表格
      </div>
    );
  }

  const scopedStyles = `
    .z-mdtbl-wrapper { overflow: auto;  position: relative; }
    .z-mdtbl { border-collapse: separate; border-spacing: 0; background: transparent; box-shadow: 0 1px 3px rgba(16,24,40,.08), 0 1px 2px rgba(16,24,40,.06); border-radius: 8px; }
    .z-mdtbl th, .z-mdtbl td { border: 1px solid #e5e7eb; padding: 10px 14px; font-size: 13px; line-height: 1.4; color: inherit; }
    .z-mdtbl th { background: transparent; font-weight: 600; color: inherit; }
    .z-mdtbl tbody tr:nth-child(odd) { background: transparent; }
    .z-mdtbl tbody tr:hover { background: transparent; }
    .z-mdtbl td, .z-mdtbl th { white-space: nowrap; }
    .z-mdtbl thead tr:first-child th:first-child { border-top-left-radius: 8px; }
    .z-mdtbl thead tr:first-child th:last-child { border-top-right-radius: 8px; }
    .z-mdtbl tbody tr:last-child td:first-child { border-bottom-left-radius: 8px; }
    .z-mdtbl tbody tr:last-child td:last-child { border-bottom-right-radius: 8px; }
  `;

  return (
    <div ref={rootRef} className="markdown-table-mode z-mdtbl-wrapper">
      <style>{scopedStyles}</style>
      <table className="z-mdtbl">
        <thead>
          <tr>
            {table.headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * 渲染HTML内容
 */
const renderHtmlContent = (data: any, _props: FviewerProps) => {
  console.log('=== Fviewer renderHtmlContent ===');
  console.log('data:', data);
  console.log('data.content:', data.content);

  // 获取实际的HTML内容 - 兼容多种数据结构
  let htmlContent = '';
  if (typeof data.content === 'string') {
    htmlContent = data.content;
  } else if (data.content?.text) {
    // 线上环境：{ type: 'html', text: '...' }
    htmlContent = data.content.text;
  } else if (data.content?.content) {
    // 本地环境：{ content: '...' }
    htmlContent = data.content.content;
  }
  console.log('最终传递给 iframe 的 content:', htmlContent);

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <iframe
        srcDoc={htmlContent}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          background: 'white',
        }}
        title="HTML Preview"
        sandbox="allow-same-origin allow-scripts"
      />
    </div>
  );
};

/**
 * 渲染未知类型内容
 */
const renderUnknownContent = (data: any, _props: FviewerProps) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#666',
      }}
    >
      <div>
        <div>不支持的文件类型</div>
        <div style={{ fontSize: '12px', marginTop: '8px' }}>{data.fileName || '未知文件'}</div>
      </div>
    </div>
  );
};

/**
 * Fviewer核心组件
 * 负责文件类型检测、内容渲染和状态管理
 *
 * @param props - 组件属性
 * @returns Fviewer组件
 */
const Fviewer: React.FC<FviewerProps> = ({
  data,
  annotationData = [],
  totalPage = 0,
  currentPage = 1,
  setScale,
  scale: userScale, // 重命名避免冲突
  rotation = 0, // 添加rotation参数
  eventsEmit,
  styles,
  tools = {
    annotation: true,
    download: true,
    zoom: true,
    close: true,
    navigation: true,
  },
  customComponents,
  className,
  contentClass,
}) => {
  // 使用主题
  const mergedTheme = styles?.theme || defaultTheme;

  // 状态管理
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus] = useState<string>('内容加载中...');
  const [error, setError] = useState<string | null>(null);

  console.log('=== Fviewer 接收数据 ===');
  console.log('Fviewer data:', data);
  console.log('Fviewer data.content:', data?.content);
  console.log('Fviewer data.content.document:', data?.content?.document);
  console.log('Fviewer data.content.document类型:', typeof data?.content?.document);
  console.log('Fviewer data.content.document是否为null:', data?.content?.document === null);
  console.log(
    'Fviewer data.content.document是否为undefined:',
    data?.content?.document === undefined
  );
  console.log('Fviewer currentPage:', currentPage);
  console.log('Fviewer scale:', userScale);

  // 调试：跟踪 data 引用变化导致的 effect 触发
  const prevDataRef = useRef<any>(null);
  const prevContentRef = useRef<any>(null);
  const renderCountRef = useRef<number>(0);
  renderCountRef.current += 1;
  console.log('[Fviewer] render count:', renderCountRef.current);

  // 检测文件类型
  const fileType = detectFileType(data);
  console.log('=== Fviewer 组件 ===');
  console.log('检测到的文件类型:', fileType);
  console.log('完整数据:', data);

  // 根据文件类型渲染内容
  const renderContent = () => {
    console.log('开始渲染内容，文件类型:', fileType);

    switch (fileType) {
      case 'pdf':
      case 'pdf_slides':
        console.log(
          '计算下，渲染PDF内容:',
          fileType,
          'pdfStartPage:' + data.pdfStartPage,
          'currentPage:' + currentPage
        );
        return renderPdfContent(
          data,
          {
            data,
            annotationData,
            totalPage,
            currentPage,
            scale: userScale,
            eventsEmit,
            styles,
            tools,
            className,
            contentClass,
          },
          fileType === 'pdf_slides',
          setScale
        );
      case 'image':
        console.log('渲染图片内容');
        return renderImageContent(data, {
          data,
          annotationData,
          scale: userScale,
          rotation,
          eventsEmit,
          styles,
          tools,
          className,
          contentClass,
        });
      case 'text':
        console.log('渲染文本内容');
        return renderTextContent(data, {
          data,
          annotationData,
          scale: userScale,
          eventsEmit,
          styles,
          tools,
          className,
          contentClass,
        });
      case 'markdown':
      case 'markdown_table':
        console.log('渲染Markdown内容:', fileType);
        if (fileType === 'markdown_table') {
          const md = typeof data.content === 'string' ? data.content : data.content?.content || '';
          const tableAnnotations = (annotationData || []).filter(
            anno => 'offsets' in anno && Array.isArray((anno as any).offsets)
          ) as TableAnnotation[];
          return <MinimalMarkdownTable markdown={md} tableAnnotations={tableAnnotations} />;
        }
        return (
          <MarkdownContentView
            data={data}
            parentProps={{
              data,
              annotationData,
              eventsEmit,
              styles,
              tools,
              className,
              contentClass,
            }}
            isTableMode={false}
          />
        );
      case 'html':
        console.log('渲染HTML内容');
        return renderHtmlContent(data, {
          data,
          annotationData,
          eventsEmit,
          styles,
          tools,
          className,
          contentClass,
        });
      default:
        console.log('渲染未知类型内容');
        return renderUnknownContent(data, {
          data,
          annotationData,
          eventsEmit,
          styles,
          tools,
          className,
          contentClass,
        });
    }
  };

  // 监听数据变化（调试数据引用变化与状态更新）
  React.useEffect(() => {
    const prevData = prevDataRef.current;
    const prevContent = prevContentRef.current;
    const dataChanged = prevData !== data;
    const contentChanged = prevContent !== (data ? data.content : undefined);
    console.log('[Fviewer useEffect:data] triggered', {
      dataChanged,
      contentChanged,
      hasData: !!data,
      hasContent: !!data?.content,
      isLoadingBefore: isLoading,
    });
    // 仅记录引用变更，避免在这里 setState 造成循环
    prevDataRef.current = data;
    prevContentRef.current = data ? data.content : undefined;
  }, [data?.content]);

  // 监听错误
  React.useEffect(() => {
    if (error) {
      setIsLoading(false);
    }
  }, [error]);

  // 监听标注数据变化
  React.useEffect(() => {
    console.log('=== Fviewer annotationData 变化监听 ===');
    console.log('annotationData:', annotationData);
    console.log('annotationData 长度:', annotationData?.length || 0);
    console.log('当前文件类型:', fileType);
  }, [annotationData, fileType]);

  return (
    <ContentContainer $theme={mergedTheme} className={contentClass}>
      {isLoading ? (
        customComponents?.LoadingComponent ? (
          <customComponents.LoadingComponent status={loadingStatus} theme={mergedTheme} />
        ) : (
          <LoadingContainer $theme={mergedTheme}>{loadingStatus}</LoadingContainer>
        )
      ) : error ? (
        customComponents?.ErrorComponent ? (
          <customComponents.ErrorComponent error={error} theme={mergedTheme} />
        ) : (
          <ErrorContainer $theme={mergedTheme}>
            <div>加载失败</div>
            <div style={{ fontSize: '12px', marginTop: '8px' }}>{error}</div>
          </ErrorContainer>
        )
      ) : (
        renderContent()
      )}
    </ContentContainer>
  );
};

export default Fviewer;
